import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { OuroBadge, CashbackBadge, PlanPill } from "../components/Badges";
import { MapPin, Phone, MessageCircle, Globe, Instagram, ArrowLeft, Loader2, ShoppingBag, ExternalLink, Star, Plus, Bike, Store, Clock } from "lucide-react";
import RouteButton from "../components/RouteButton";
import VideoPlayer from "../components/VideoPlayer";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

export default function CompanyDetail() {
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/companies/${id}`)
      .then((r) => setCompany(r.data))
      .catch(() => setCompany(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="p-10 text-stone-500 flex items-center gap-2"><Loader2 className="animate-spin"/> Carregando...</div>;
  }

  if (!company) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <h1 className="font-display text-2xl font-bold">Empresa não encontrada</h1>
        <Link to="/" className="text-blue-900 underline">← Voltar para o início</Link>
      </div>
    );
  }

  const isOuro = company.plan === "OURO";
  const showCashback = company.accepts_cashback && (company.plan === "PRATA" || company.plan === "OURO");
  const images = (company.media || []).filter((m) => m.kind === "image" || !m.kind);
  const videos = (company.media || []).filter((m) => m.kind === "video");

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
      <Link to={`/discover/${company.city_slug}`} className="inline-flex items-center gap-1 text-xs sm:text-sm text-stone-500 hover:text-stone-900 mb-3 sm:mb-6" data-testid="company-back">
        <ArrowLeft size={14}/> Voltar para o mapa
      </Link>

      {/* Cover: dedicated cover_url first; fallback to first gallery image */}
      <div className={`relative overflow-hidden rounded-2xl sm:rounded-3xl ${isOuro ? "ouro-glow border-2 border-amber-400" : "border border-stone-200"} bg-gradient-to-br from-blue-50 to-stone-100 h-48 sm:h-72 mb-4 sm:mb-6`}>
        {company.cover_url ? (
          <img src={company.cover_url} alt={company.name} className="w-full h-full object-cover" data-testid="company-cover"/>
        ) : images[0] ? (
          <img src={images[0].url} alt={company.name} className="w-full h-full object-cover"/>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-stone-300 font-display text-7xl">{company.name[0]}</div>
        )}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-blue-950/90 to-transparent p-6 text-white">
          <div className="flex items-center gap-3">
            {company.logo_url && (
              <img src={company.logo_url} alt="logo" className="w-16 h-16 rounded-2xl object-contain bg-white border-2 border-white shadow-lg shrink-0 p-1"/>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <PlanPill plan={company.plan}/>
                {isOuro && <OuroBadge/>}
                {showCashback && <CashbackBadge/>}
              </div>
              <h1 className="font-display text-2xl sm:text-4xl font-bold leading-tight">{company.name}</h1>
              <p className="text-white/85 text-xs sm:text-sm mt-1">{company.category}{company.address ? ` · ${company.address}` : ""}</p>
              <div className="mt-2 sm:mt-3">
                <RouteButton lat={company.lat} lng={company.lng} label={`${company.name} - ${company.address}`} size="sm"/>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          <Card className="p-4 sm:p-6 rounded-2xl border-stone-200">
            <h2 className="font-display text-lg sm:text-xl font-semibold mb-2">Sobre</h2>
            <p className="text-stone-700 text-sm sm:text-base leading-relaxed">{company.description || "Sem descrição disponível."}</p>
          </Card>

          {(() => {
            // Se há cover_url dedicado, a galeria mostra TODAS as imagens; senão pula a 1ª (já usada como capa)
            const galleryImages = company.cover_url ? images : images.slice(1);
            if (galleryImages.length === 0) return null;
            return (
              <Card className="p-6 rounded-2xl border-stone-200">
                <h2 className="font-display text-xl font-semibold mb-4">Galeria</h2>
                <div className="grid sm:grid-cols-3 gap-3">
                  {galleryImages.map((m, i) => (
                    <img key={i} src={m.url} alt={m.caption || ""} className="aspect-square w-full object-cover rounded-xl"/>
                  ))}
                </div>
              </Card>
            );
          })()}

          {videos.length > 0 && (
            <Card className="p-6 rounded-2xl border-stone-200">
              <h2 className="font-display text-xl font-semibold mb-4">Vídeos</h2>
              <div className="space-y-4">
                {videos.map((m, i) => (
                  <VideoPlayer key={i} src={m.url} title={m.caption}/>
                ))}
              </div>
            </Card>
          )}

          {company.plan === "OURO" && <ProductsSection companyId={company.id} companyName={company.name}/>}

          <ReviewsSection companyId={company.id} initialAvg={company.avg_rating} initialCount={company.rating_count}/>
        </div>

        <div className="space-y-4">
          <Card className="p-6 rounded-2xl border-stone-200">
            <h3 className="font-display text-base font-semibold mb-3">Contato</h3>
            <div className="space-y-2.5 text-sm">
              {company.address && (
                <a href={`https://www.google.com/maps/search/${encodeURIComponent(company.address + ' ' + company.city_slug)}`} target="_blank" rel="noreferrer" className="flex items-start gap-2 text-stone-700 hover:text-blue-900">
                  <MapPin size={16} className="mt-0.5 text-blue-900"/> <span>{company.address}</span>
                </a>
              )}
              {company.phone && (
                <a href={`tel:${company.phone}`} className="flex items-center gap-2 text-stone-700 hover:text-blue-900">
                  <Phone size={16} className="text-blue-900"/> {company.phone}
                </a>
              )}
              {company.whatsapp && (
                <a href={`https://wa.me/${company.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-stone-700 hover:text-blue-900">
                  <MessageCircle size={16} className="text-blue-900"/> WhatsApp: {company.whatsapp}
                </a>
              )}
              {company.instagram && (
                <a href={`https://instagram.com/${company.instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-stone-700 hover:text-blue-900">
                  <Instagram size={16} className="text-blue-900"/> @{company.instagram.replace(/^@/, "")}
                </a>
              )}
              {company.website && (
                <a href={company.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-stone-700 hover:text-blue-900">
                  <Globe size={16} className="text-blue-900"/> Site oficial
                </a>
              )}
              {!company.phone && !company.whatsapp && !company.instagram && !company.website && (
                <p className="text-xs text-stone-400">Contatos não cadastrados ainda.</p>
              )}
            </div>
          </Card>

          {showCashback && (
            <Card className="p-4 sm:p-6 rounded-2xl border-emerald-300 bg-emerald-50">
              <h3 className="font-display text-base font-semibold text-emerald-900 mb-1">BemCash disponível</h3>
              <p className="text-sm text-emerald-800">Compre aqui e ganhe <strong>5% de cashback</strong> direto na sua carteira global Morar Bem.</p>
            </Card>
          )}

          <Card className="p-4 sm:p-6 rounded-2xl border-stone-200">
            <h3 className="font-display text-base font-semibold mb-3">Atendimento</h3>
            <div className="space-y-2.5 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                {company.has_delivery ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-100 text-emerald-900 border border-emerald-300 text-xs font-bold uppercase tracking-wider"><Bike size={12}/> Faz Delivery</span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-stone-100 text-stone-600 border border-stone-200 text-xs font-bold uppercase tracking-wider"><Bike size={12}/> Sem delivery</span>
                )}
                {company.pickup_available !== false && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-100 text-blue-900 border border-blue-300 text-xs font-bold uppercase tracking-wider"><Store size={12}/> Retirada em loja</span>
                )}
              </div>
              {company.business_hours && (
                <div className="flex items-start gap-2 text-stone-700">
                  <Clock size={16} className="text-stone-400 mt-0.5 shrink-0"/>
                  <span className="whitespace-pre-line">{company.business_hours}</span>
                </div>
              )}
              {!company.has_delivery && (
                <div className="pt-1">
                  <RouteButton lat={company.lat} lng={company.lng} label={`${company.name} - ${company.address || ''}`} size="sm"/>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function ProductsSection({ companyId, companyName }) {
  const [data, setData] = React.useState({ items: [], total: 0, pages: 0, categories: [] });
  const [page, setPage] = React.useState(1);
  const [category, setCategory] = React.useState("");
  const [kind, setKind] = React.useState("");
  const { addItem, cart } = useCart() || {};
  const { user } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    const params = new URLSearchParams({ page, page_size: 8 });
    if (category) params.set("category", category);
    if (kind) params.set("kind", kind);
    api.get(`/products/company/${companyId}?${params.toString()}`).then((r) => setData(r.data));
  }, [companyId, page, category, kind]);

  const addToCart = (p) => {
    if (!user) { toast.info("Faça login pra adicionar ao carrinho"); navigate("/login"); return; }
    if (user.role !== "CUSTOMER") { toast.error("Apenas clientes podem comprar"); return; }
    addItem(companyId, companyName, {
      product_id: p.id, title: p.title, unit_price: p.price,
      image_url: p.image_url, cashback_pct_on_checkout: p.cashback_pct_on_checkout || 0,
      turbo_cashback_pct_on_checkout: p.turbo_active ? (p.turbo_cashback_pct || 0) : 0,
    });
    toast.success(`${p.title} adicionado ao carrinho!`);
  };

  if (data.total === 0 && !category && !kind) return null;

  return (
    <Card className="p-3 sm:p-6 rounded-2xl border-stone-200" data-testid="products-section">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3 sm:mb-4">
        <h2 className="font-display text-lg sm:text-xl font-semibold flex items-center gap-2"><ShoppingBag size={18}/> Produtos & Serviços</h2>
        <div className="flex gap-1.5">
          <button onClick={() => setKind("")} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${kind === "" ? "bg-blue-900 text-white" : "bg-stone-100 text-stone-700"}`}>Tudo</button>
          <button onClick={() => setKind("product")} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${kind === "product" ? "bg-blue-900 text-white" : "bg-stone-100 text-stone-700"}`}>Produtos</button>
          <button onClick={() => setKind("service")} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${kind === "service" ? "bg-blue-900 text-white" : "bg-stone-100 text-stone-700"}`}>Serviços</button>
        </div>
      </div>
      {data.categories.length > 0 && (
        <div className="flex gap-1.5 mb-4 flex-wrap">
          <button onClick={() => setCategory("")} className={`px-2 py-0.5 rounded-md text-xs ${category === "" ? "bg-amber-100 text-amber-900 border border-amber-300" : "bg-stone-100 text-stone-600"}`}>Todas</button>
          {data.categories.map((c) => (
            <button key={c} onClick={() => setCategory(c)} className={`px-2 py-0.5 rounded-md text-xs ${category === c ? "bg-amber-100 text-amber-900 border border-amber-300" : "bg-stone-100 text-stone-600"}`}>{c}</button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
        {data.items.map((p) => (
          <Link to={`/product/${p.id}`} key={p.id} className="rounded-xl sm:rounded-2xl border border-stone-200 bg-white overflow-hidden hover:shadow-md transition-shadow block" data-testid={`product-${p.id}`}>
            {p.image_url && <img src={p.image_url} alt={p.title} className="w-full aspect-square object-cover"/>}
            <div className="p-2 sm:p-3">
              <div className="flex items-center gap-1 mb-0.5 sm:mb-1 flex-wrap">
                <span className="text-[9px] font-bold uppercase tracking-wider px-1 sm:px-1.5 py-0.5 rounded bg-stone-100 text-stone-700">{p.kind === "service" ? "Serv" : "Prod"}</span>
                {p.cashback_pct_on_checkout > 0 && <span className="text-[9px] font-bold uppercase px-1 sm:px-1.5 py-0.5 rounded bg-amber-100 text-amber-900">+{p.cashback_pct_on_checkout}%</span>}
              </div>
              <h3 className="font-display font-semibold text-stone-900 leading-tight text-xs sm:text-sm line-clamp-2 min-h-[2.25rem]">{p.title}</h3>
              <div className="flex items-center justify-between mt-1.5 sm:mt-2 gap-1">
                {p.price > 0 ? <span className="font-display text-sm sm:text-base font-bold text-blue-950">R$ {p.price.toFixed(2)}</span> : <span className="text-[10px] text-stone-400">Sob consulta</span>}
                {p.checkout_enabled && p.price > 0 && (
                  <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); addToCart(p); }} className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-blue-900 text-white hover:bg-blue-950 shrink-0" data-testid={`add-to-cart-${p.id}`}>
                    <Plus size={12}/>
                  </button>
                )}
                {p.sale_url && !p.checkout_enabled && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-blue-900">
                    Ver <ExternalLink size={9}/>
                  </span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-5">
          {Array.from({ length: data.pages }, (_, i) => i + 1).map((n) => (
            <button key={n} onClick={() => setPage(n)} className={`w-8 h-8 rounded-lg text-xs font-semibold ${page === n ? "bg-blue-900 text-white" : "bg-stone-100 text-stone-700"}`} data-testid={`prod-page-${n}`}>{n}</button>
          ))}
        </div>
      )}
    </Card>
  );
}

function ReviewsSection({ companyId, initialAvg, initialCount }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [avg, setAvg] = useState(initialAvg || 0);
  const [count, setCount] = useState(initialCount || 0);
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/companies/${companyId}/reviews`);
    setReviews(data);
    if (data.length > 0) {
      const a = data.reduce((s, r) => s + r.rating, 0) / data.length;
      setAvg(parseFloat(a.toFixed(2)));
      setCount(data.length);
    } else { setAvg(0); setCount(0); }
    const mine = data.find((r) => r.customer_id === user?.id);
    if (mine) { setRating(mine.rating); setComment(mine.comment || ""); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId, user?.id]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/reviews`, { rating, comment });
      toast.success("Avaliação enviada!");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-6 rounded-2xl border-stone-200" data-testid="reviews-section">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h2 className="font-display text-xl font-semibold flex items-center gap-2"><Star size={20} className="text-amber-500 fill-current"/> Avaliações</h2>
        {count > 0 && <div className="text-sm text-stone-600">Média <strong className="text-amber-700">{avg.toFixed(1)}</strong> · {count} {count === 1 ? "avaliação" : "avaliações"}</div>}
      </div>

      {user?.role === "CUSTOMER" && (
        <div className="mb-5 p-4 rounded-xl bg-amber-50 border border-amber-200">
          <div className="text-xs font-bold uppercase tracking-wider text-amber-900 mb-2">Sua nota</div>
          <div className="flex items-center gap-1 mb-3" data-testid="rating-selector">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setRating(n)}
                className="text-amber-500" data-testid={`star-${n}`}>
                <Star size={26} className={(hover || rating) >= n ? "fill-current" : ""}/>
              </button>
            ))}
          </div>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Conta como foi sua experiência (opcional)..."
            className="rounded-xl bg-white" maxLength={500} data-testid="review-comment"/>
          <div className="flex justify-end mt-2">
            <Button onClick={submit} disabled={busy} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="review-submit">
              {busy ? "Enviando..." : "Enviar avaliação"}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {reviews.length === 0 && <p className="text-sm text-stone-500">Seja o primeiro a avaliar.</p>}
        {reviews.map((r) => (
          <div key={r.id} className="p-3 rounded-xl border border-stone-200 bg-white" data-testid={`review-${r.id}`}>
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-stone-900">{r.customer_name}</div>
              <div className="flex items-center gap-0.5 text-amber-500">
                {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={12} className={n <= r.rating ? "fill-current" : ""}/>)}
              </div>
            </div>
            {r.comment && <p className="text-sm text-stone-600 mt-1.5 whitespace-pre-line">{r.comment}</p>}
            <div className="text-[10px] text-stone-400 mt-1">{new Date(r.created_at).toLocaleString("pt-BR")}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}
